class SearchOverlay {
  constructor() {
    this.overlay = document.querySelector('#search-overlay');
    if (!this.overlay) return;

    this.input = this.overlay.querySelector('[data-search-input]');
    this.results = this.overlay.querySelector('[data-search-results]');
    this.loadingState = this.overlay.querySelector('[data-search-loading]');
    this.resultsContent = this.overlay.querySelector('[data-search-content]');
    this.noResultsState = this.overlay.querySelector('[data-search-no-results]');
    this.productsSection = this.overlay.querySelector('[data-search-products]');
    this.productsList = this.overlay.querySelector('[data-products-list]');
    this.productsCount = this.overlay.querySelector('[data-products-count]');
    this.suggestionsSection = this.overlay.querySelector('[data-search-suggestions]');
    this.suggestionsList = this.overlay.querySelector('[data-suggestions-list]');
    this.articlesSection = this.overlay.querySelector('[data-search-articles]');
    this.articlesList = this.overlay.querySelector('[data-articles-list]');
    this.articlesCount = this.overlay.querySelector('[data-articles-count]');
    this.noProductsMessage = this.overlay.querySelector('[data-no-products-message]');
    this.noArticlesMessage = this.overlay.querySelector('[data-no-articles-message]');
    this.noSuggestionsMessage = this.overlay.querySelector('[data-no-suggestions-message]');

    // Get translations from data attributes
    this.reviewsText = this.overlay.dataset.reviewsText || 'avis';
    this.noResultsText = this.overlay.dataset.noResultsText || 'Aucun résultat trouvé pour "{query}"';
    this.noProductsCtaUrl = this.overlay.dataset.noProductsCtaUrl || '/pages/newsletter';
    this.noProductsCtaLabel = this.overlay.dataset.noProductsCtaLabel || "S'inscrire à la newsletter";

    this.searchTimeout = null;
    this.currentQuery = '';
    this.abortController = null;

    // Keyboard navigation
    this.currentFocusIndex = -1;
    this.focusableElements = [];

    this.init();
  }

  init() {
    // Open buttons
    document.querySelectorAll('[data-action="search-overlay-open"]').forEach((button) => {
      button.addEventListener('click', () => this.open());
    });

    // Close buttons and backdrop
    this.overlay.querySelectorAll('[data-action="search-overlay-close"]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    // Close on escape key and keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!this.overlay.classList.contains('is-open')) return;

      if (e.key === 'Escape') {
        this.close();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.navigateResults(1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.navigateResults(-1);
      } else if (e.key === 'Enter' && this.currentFocusIndex >= 0) {
        e.preventDefault();
        this.selectCurrentResult();
      }
    });

    // Search input
    this.input.addEventListener('input', () => this.handleInput());
  }

  open() {
    // Calculate header + visible announcement bar height
    const header = document.querySelector('.section-apotech-header');
    const announcementBar = document.querySelector('embla-carousel');
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop;

    let topOffset = 0;

    // Add header height
    if (header) {
      topOffset += header.offsetHeight;
    }

    // Add only the visible part of announcement bar
    if (announcementBar) {
      const announcementHeight = announcementBar.offsetHeight;
      const visibleAnnouncementHeight = Math.max(0, announcementHeight - currentScroll);
      topOffset += visibleAnnouncementHeight;
    }

    // Set the top position and height dynamically
    this.overlay.style.top = `${topOffset}px`;
    this.overlay.style.height = `calc(100vh - ${topOffset}px)`;

    this.overlay.classList.add('is-open');
    document.body.classList.add('search-overlay-open');

    // Trigger reflow for animation
    this.overlay.offsetHeight;

    this.overlay.classList.add('is-visible');
    this.overlay.setAttribute('aria-hidden', 'false');

    // Focus input
    setTimeout(() => {
      this.input.focus();
    }, 100);
  }

  close() {
    this.overlay.classList.remove('is-visible');
    this.overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('search-overlay-open');

    // Reset ARIA attributes
    this.input.setAttribute('aria-expanded', 'false');

    setTimeout(() => {
      this.overlay.classList.remove('is-open');
      this.input.value = '';
      this.currentQuery = '';
      this.results.classList.remove('has-results');
      this.clearResults();
      this.resetKeyboardNavigation();
    }, 300);
  }

  handleInput() {
    const query = this.input.value.trim();

    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Cancel previous request
    if (this.abortController) {
      this.abortController.abort();
    }

    // If empty, hide all results and remove padding
    if (query === '') {
      this.clearResults();
      this.hideLoading();
      this.hideNoResults();
      this.results.classList.remove('has-results');
      this.input.setAttribute('aria-expanded', 'false');
      return;
    }

    // Update ARIA for expanded state
    this.input.setAttribute('aria-expanded', 'true');

    // Debounce search
    this.searchTimeout = setTimeout(() => {
      this.search(query);
    }, 300);
  }

  async search(query) {
    if (query === this.currentQuery) return;
    this.currentQuery = query;

    // Show loading
    this.showLoading();

    // Create new abort controller
    this.abortController = new AbortController();

    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,article,collection,query&resources[limit]=20&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor,tag,body&section_id=predictive-search`,
        { signal: this.abortController.signal }
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      console.log('Search API response:', data);
      console.log('First product metafields:', data.resources?.results?.products?.[0]);
      this.displayResults(data);
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Search request was cancelled');
      } else {
        console.error('Search error:', error);
        this.hideLoading();
      }
    }
  }

  displayResults(data) {
    this.hideLoading();
    this.hideNoResults();
    this.clearResults();

    const products = data.resources.results.products || [];
    const articles = data.resources.results.articles || [];
    const collections = data.resources.results.collections || [];
    const queries = data.resources.results.queries || [];

    console.log('Products:', products.length, 'Articles:', articles.length, 'Collections:', collections.length, 'Queries:', queries.length);

    // Check if we have any results
    const hasResults = products.length > 0 || articles.length > 0 || collections.length > 0;

    if (!hasResults) {
      this.showNoResults();
      return;
    }

    // Add padding when there are results
    this.results.classList.add('has-results');

    // Show results container
    this.resultsContent.style.display = 'grid';

    // Suggestions and collections are disabled
    // Do not display suggestions or collections

    // Display products
    if (products.length > 0) {
      this.displayProducts(products);
    } else {
      // Show no products message with CTA if there are other results (articles/collections)
      if (this.noProductsMessage && this.productsSection) {
        this.displayNoProductsMessage();
        this.productsSection.style.display = 'flex';
      }
    }

    // Display articles
    if (articles.length > 0) {
      this.displayArticles(articles);
    } else {
      // Show no articles message
      if (this.noArticlesMessage && this.articlesSection) {
        this.noArticlesMessage.style.display = 'block';
        this.articlesSection.style.display = 'block';
      }
    }

    // Reset keyboard navigation after displaying results
    this.resetKeyboardNavigation();
  }

  displayProducts(products) {
    if (!this.productsList || !this.productsSection) return;

    console.log('Displaying products:', products);

    // Limit to 4 products on desktop
    const isDesktop = window.innerWidth > 989;
    const displayLimit = isDesktop ? 4 : products.length;
    const productsToDisplay = products.slice(0, displayLimit);
    const hasMore = products.length > displayLimit;

    // Get product data from pre-loaded global object (no fetch needed)
    const productDataMap = window.SOVA_PRODUCTS_DATA || {};

    // Render product cards with pre-loaded data
    this.productsList.innerHTML = productsToDisplay
      .map((product) => this.createProductCard(product, productDataMap[product.id]))
      .join('');

    if (this.productsCount) {
      this.productsCount.textContent = `(${products.length})`;
    }

    // Add "See more" link in header if needed
    const sectionHeader = this.productsSection.querySelector('.search-overlay__section-header');
    const existingSeeMore = sectionHeader.querySelector('.search-overlay__see-more');

    if (existingSeeMore) {
      existingSeeMore.remove();
    }

    if (hasMore && sectionHeader) {
      const seeMoreLink = document.createElement('a');
      seeMoreLink.href = `/search?q=${encodeURIComponent(this.currentQuery)}`;
      seeMoreLink.className = 'search-overlay__see-more p-extra-small';
      seeMoreLink.style.fontWeight = '600';
      seeMoreLink.style.textDecoration = 'underline';
      seeMoreLink.textContent = 'VOIR TOUT';
      seeMoreLink.setAttribute('data-search-link', '');
      sectionHeader.appendChild(seeMoreLink);
    }

    this.productsSection.style.display = 'flex';

    // Add click listeners to close overlay
    this.addLinkClickListeners();
  }

  createProductCard(product, fetchedData = null) {
    const price = this.formatPrice(product.price);

    // Get product image
    const imageUrl = product.featured_image
      ? product.featured_image.url
      : '';

    // Get title (remove variant part if exists)
    let title = product.title;
    if (title.includes(' - ')) {
      title = title.split(' - ')[0];
    }

    // Get subtitle and headline from fetched data (Section Rendering API)
    let subtitle = fetchedData?.subtitle || '';
    let headline = fetchedData?.headline || '';

    // Fallback: if no data from fetch, try to extract from title
    if (!subtitle && product.title.includes(' - ')) {
      subtitle = product.title.split(' - ')[1];
    }

    // Use headline as fallback if no subtitle
    const displaySubtitle = subtitle || headline;

    // Apply highlighting to title and subtitle
    const highlightedTitle = this.highlightText(title, this.currentQuery);
    const highlightedSubtitle = displaySubtitle ? this.highlightText(displaySubtitle, this.currentQuery) : '';

    // Get reviews data from fetched data
    const rating = fetchedData?.rating || 0;
    const ratingCount = fetchedData?.rating_count || 0;

    // Generate stars HTML
    let starsHtml = '';
    if (ratingCount > 0) {
      starsHtml = this.createStarsHtml(rating, ratingCount);
    }

    return `
      <div class="search-product-card">
        <div class="search-product-card__image-wrapper">
          <a href="${product.url}" class="search-product-card__image-link" tabindex="-1" data-search-link>
            ${imageUrl ? `<img src="${imageUrl}" alt="${title}" class="search-product-card__image" loading="lazy">` : ''}
          </a>
          ${starsHtml}
        </div>
        <div class="search-product-card__info">
          <div class="search-product-card__title-price">
            <a href="${product.url}" class="search-product-card__title p-medium" style="font-family: var(--font-family-paragraph-alternate); font-style: italic;" data-search-link>
              ${highlightedTitle}
            </a>
            <div class="search-product-card__price-wrapper">
              <span class="search-product-card__price-from">Dès</span>&nbsp;<span class="search-product-card__price">${price}</span>
            </div>
          </div>
          ${highlightedSubtitle ? `<div class="search-product-card__subtitle p-extra-small">${highlightedSubtitle}</div>` : ''}
        </div>
      </div>
    `;
  }

  displayCollections(collections) {
    if (!this.suggestionsSection || !this.suggestionsList) return;

    // Add collections to suggestions list
    const collectionsHtml = collections
      .map((collection) => `
        <li class="search-overlay__suggestion-item">
          <a href="${collection.url}" class="search-overlay__suggestion-link p-extra-small" data-search-link>
            ${collection.title}
          </a>
        </li>
      `)
      .join('');

    // If there are static suggestions, append collections to them
    const existingSuggestions = this.suggestionsList.innerHTML;
    this.suggestionsList.innerHTML = existingSuggestions + collectionsHtml;

    this.suggestionsSection.style.display = 'block';

    // Add click listeners to close overlay
    this.addLinkClickListeners();
  }

  createStarsHtml(rating, ratingCount) {
    const ratingFloat = Math.round(rating * 10) / 10;
    let starsHtml = '<div class="custom-product-cards-review flex items-center gap-x-1">';

    // Stars container
    starsHtml += '<div class="inline-flex items-center gap-x-0.5" style="color: #352045;">';
    for (let i = 1; i <= 5; i++) {
      if (ratingFloat >= i) {
        starsHtml += '<svg class="size-3" viewBox="0 0 24 24" fill="currentColor" preserveAspectRatio="xMidYMid meet" aria-hidden="true" focusable="false" role="presentation"><path fill-rule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clip-rule="evenodd"></path></svg>';
      } else if (ratingFloat >= i - 0.5) {
        starsHtml += '<svg class="size-3" viewBox="0 0 24 24" fill="currentColor" preserveAspectRatio="xMidYMid meet" aria-hidden="true" focusable="false" role="presentation"><path fill-rule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clip-rule="evenodd"></path></svg>';
      } else {
        starsHtml += '<svg class="size-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" preserveAspectRatio="xMidYMid meet" aria-hidden="true" focusable="false" role="presentation"><path stroke-linecap="round" stroke-linejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"></path></svg>';
      }
    }
    starsHtml += '</div>';

    // Rating count
    starsHtml += `<span class="typo-body-xs opacity-80 leading-none">${ratingCount} ${this.reviewsText}</span>`;
    starsHtml += '</div>';

    return starsHtml;
  }

  displayAPISuggestions(queries) {
    if (!this.suggestionsList || !this.suggestionsSection) return;

    this.suggestionsList.innerHTML = queries
      .map((query) => `
        <li class="search-overlay__suggestion-item">
          <a href="/search?q=${encodeURIComponent(query.text)}" class="search-overlay__suggestion-link p-extra-small" data-search-link>
            ${query.styled_text || this.highlightText(query.text, this.currentQuery)}
          </a>
        </li>
      `)
      .join('');

    this.suggestionsSection.style.display = 'block';

    // Add click listeners to close overlay
    this.addLinkClickListeners();
  }

  showStaticSuggestions() {
    if (!this.suggestionsSection) return;

    // Just show the section with the static suggestions from Liquid
    this.suggestionsSection.style.display = 'block';
  }

  displayArticles(articles) {
    if (!this.articlesList || !this.articlesSection) return;

    this.articlesList.innerHTML = articles
      .map((article) => `
        <li class="search-overlay__article-item">
          <a href="${article.url}" class="search-overlay__article-link p-extra-small" data-search-link>
            ${this.highlightText(article.title, this.currentQuery)}
          </a>
        </li>
      `)
      .join('');

    if (this.articlesCount) {
      this.articlesCount.textContent = `(${articles.length})`;
    }

    this.articlesSection.style.display = 'block';

    // Add click listeners to close overlay
    this.addLinkClickListeners();
  }

  displayNoProductsMessage() {
    if (!this.noProductsMessage) return;

    // Get the message content from the data attribute
    const messageContent = this.overlay.dataset.noProductsMessage || '';

    // Create container with message and CTA button
    const ctaButton = this.noProductsCtaUrl ? `
      <a href="${this.noProductsCtaUrl}" class="search-overlay__no-products-cta button button-primary" data-search-link>
        ${this.noProductsCtaLabel}
      </a>
    ` : '';

    this.noProductsMessage.innerHTML = `
      <div class="search-overlay__no-products-content">
        ${messageContent}
        ${ctaButton}
      </div>
    `;

    this.noProductsMessage.style.display = 'block';

    // Add click listeners to close overlay
    this.addLinkClickListeners();
  }

  showNoResults() {
    const message = this.noResultsText.replace('{query}', this.currentQuery);
    this.noResultsState.innerHTML = `
      <p style="font-family: var(--font-family-paragraph, 'Surt Normal'); font-size: 16px; line-height: 22px; color: rgba(53, 32, 69, 0.6); text-align: center; padding: 48px 0;">
        ${message}
      </p>
    `;
    this.noResultsState.style.display = 'block';
    this.results.classList.add('has-results');
  }

  hideNoResults() {
    if (this.noResultsState) {
      this.noResultsState.style.display = 'none';
    }
  }

  clearResults() {
    if (this.productsList) this.productsList.innerHTML = '';
    if (this.articlesList) this.articlesList.innerHTML = '';

    // Reset suggestions list to only static suggestions from Liquid
    if (this.suggestionsList) {
      const staticSuggestions = this.suggestionsList.querySelectorAll('[shopify-section-id]');
      this.suggestionsList.innerHTML = '';
      staticSuggestions.forEach(item => this.suggestionsList.appendChild(item.cloneNode(true)));
    }

    // Hide all "no results" messages
    if (this.noProductsMessage) this.noProductsMessage.style.display = 'none';
    if (this.noArticlesMessage) this.noArticlesMessage.style.display = 'none';
    if (this.noSuggestionsMessage) this.noSuggestionsMessage.style.display = 'none';

    if (this.productsSection) this.productsSection.style.display = 'none';
    if (this.articlesSection) this.articlesSection.style.display = 'none';
    if (this.suggestionsSection) this.suggestionsSection.style.display = 'none';
    if (this.resultsContent) this.resultsContent.style.display = 'none';
  }

  showLoading() {
    this.clearResults();
    if (this.loadingState) {
      this.loadingState.style.display = 'flex';
    }
  }

  hideLoading() {
    if (this.loadingState) {
      this.loadingState.style.display = 'none';
    }
  }

  formatPrice(price) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: Shopify.currency.active || 'EUR',
    }).format(price);
  }

  stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  // Highlight search terms in text (disabled)
  highlightText(text) {
    return text;
  }

  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // Keyboard navigation methods
  navigateResults(direction) {
    this.updateFocusableElements();

    if (this.focusableElements.length === 0) return;

    // Remove current focus
    if (this.currentFocusIndex >= 0 && this.focusableElements[this.currentFocusIndex]) {
      this.focusableElements[this.currentFocusIndex].classList.remove('search-overlay__item--focused');
    }

    // Update index
    this.currentFocusIndex += direction;

    // Wrap around
    if (this.currentFocusIndex < 0) {
      this.currentFocusIndex = this.focusableElements.length - 1;
    } else if (this.currentFocusIndex >= this.focusableElements.length) {
      this.currentFocusIndex = 0;
    }

    // Add new focus
    if (this.focusableElements[this.currentFocusIndex]) {
      this.focusableElements[this.currentFocusIndex].classList.add('search-overlay__item--focused');
      this.focusableElements[this.currentFocusIndex].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  updateFocusableElements() {
    this.focusableElements = Array.from(
      this.resultsContent.querySelectorAll(
        '.search-overlay__suggestion-link, .search-product-card__image-link, .search-overlay__article-link'
      )
    );
  }

  selectCurrentResult() {
    if (this.currentFocusIndex >= 0 && this.focusableElements[this.currentFocusIndex]) {
      this.focusableElements[this.currentFocusIndex].click();
    }
  }

  resetKeyboardNavigation() {
    this.currentFocusIndex = -1;
    this.focusableElements.forEach(el => el.classList.remove('search-overlay__item--focused'));
  }

  addLinkClickListeners() {
    // Add click listeners to all search result links
    const links = this.resultsContent.querySelectorAll('[data-search-link]');
    links.forEach(link => {
      link.addEventListener('click', () => {
        this.close();
      });
    });
  }
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new SearchOverlay();
  });
} else {
  new SearchOverlay();
}
